import { Feature, Map } from "ol";
import Geometry from "ol/geom/Geometry";
// import { getCenter } from "ol/extent";
import Polygon from "ol/geom/Polygon";
import VectorLayer from "ol/layer/Vector";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { PortDetailsDto, PortListDto, usePortApi } from "~/api/GeneratedApi";
import { setFocusedPort } from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { findLayerByTitle } from "../functions/findLayerByTitle";
import { LayerTitle } from "../types/layers";

interface UsePortFocusArgs {
  map?: Map;
}

interface UsePortFocusReturn {
  portFocus: (args: PortFocusArgs) => void;
  focusedPort?: PortDetailsDto;
  handleFocusRemove: () => void;
  refocus: (port?: PortDetailsDto) => void;
  viewChanged: boolean;
}

interface PortFocusArgs {
  ports: PortListDto[];
}

export function usePortFocus({ map }: UsePortFocusArgs): UsePortFocusReturn {
  const focusedPort = useAppSelector((s) => s.map.focusedPort);
  const [portToFetchId, setPortToFetchId] = useState<number>();
  const [viewChanged, setViewChanged] = useState(false);
  const { getPort } = usePortApi();
  const history = useHistory();
  const portFeatureRef = useRef<Feature<Geometry>>();
  const dispatch = useAppDispatch();

  const currentId = focusedPort?.id ?? undefined;
  const { search } = history.location;

  useCustomQuery(
    { key: "port", id: portToFetchId },
    () => getPort(portToFetchId!),
    {
      refetchOnWindowFocus: false,
      enabled: !!portToFetchId,
      onSuccess: (p) => {
        dispatch(setFocusedPort(p));
        setPortToFetchId(undefined);
        setViewChanged(false);
      },
    },
  );

  const moveToPort = useCallback(
    (port?: PortDetailsDto) => {
      if (!portFeatureRef.current) {
        const portLayer = findLayerByTitle(map, LayerTitle.Ports);
        const portFeature = (portLayer as VectorLayer)
          ?.getSource()
          ?.getFeatureById(`port${port?.id}`);
        portFeatureRef.current = portFeature;
      }
      const view = map?.getView();

      const poly = portFeatureRef.current?.getGeometry() as Polygon;

      const ext = poly.getExtent();

      view?.fit(ext);
      const curZoom = view?.getZoom();

      if (!curZoom) return;
      view?.setZoom(curZoom - 0.2);
      // Animate things maybe?
      //   const center = getCenter(ext);
      //   const res = view.getResolutionForExtent(ext);
      //   const zoom = view.getZoomForResolution(res);
      //   const duration = 1000;
      //   if (!zoom) return;
      //   view.animate({
      //     center,
      //     duration,
      //   });
      //   view.animate({
      //     zoom: zoom - 0.3,
      //     duration: duration / 2,
      //   });
    },
    [map],
  );

  const portFocus = useCallback(
    ({ ports }: PortFocusArgs) => {
      const urlParameters = new URLSearchParams(search);
      const param = urlParameters?.get("port");
      const portParameter = param && !Number.isNaN(+param) ? +param : undefined;

      if (!portParameter || !ports?.length || !map) {
        return;
      }
      const port = ports?.find((p) => p.id === portParameter);
      if (port?.id === currentId) return;

      const portLayer = findLayerByTitle(map, LayerTitle.Ports);
      const portFeature = (portLayer as VectorLayer)
        ?.getSource()
        ?.getFeatureById(`port${port?.id}`);
      if (!port || !portFeature) return;
      portFeatureRef.current = portFeature;

      setPortToFetchId(port.id);
      moveToPort();
    },
    [search, map, currentId, moveToPort],
  );

  useEffect(() => {
    const listener = () => {
      setViewChanged(true);
    };
    map?.getView();
    map?.on("moveend", listener);
    return () => {
      map?.un("moveend", listener);
    };
  }, [map]);

  const refocus = useCallback(
    (port?: PortDetailsDto) => {
      moveToPort(port);
      // Move end happens after state updates so need to add a small delay
      setTimeout(() => {
        setViewChanged(false);
      }, 500);
    },
    [moveToPort],
  );

  const handleFocusRemove = useCallback(() => {
    dispatch(setFocusedPort(undefined));
    setPortToFetchId(undefined);
    portFeatureRef.current = undefined;
    const oldParams = new URLSearchParams(history.location.search);
    oldParams.delete("port");
    history.push({
      pathname: history.location.pathname,
      search: oldParams.toString(),
    });
  }, [history, dispatch]);

  return { portFocus, focusedPort, handleFocusRemove, refocus, viewChanged };
}
